import React from 'react';

import { createRoot } from 'react-dom/client';

import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import { i18n } from '@lingui/core';
import {
  type LayoutType,
  type PlacementId,
  PlacementStatus,
  debugLog
} from '@schibsted-nmp/advertising-shared';

import { SponsoredProductAd } from './ads/SponsoredProductAd.js';
import type { RetailMediaPayload, RetailMediaProduct } from './types/index.js';
import { callApi, RateLimitError } from './utils/RetailMediaApi.js';

const request = async (
  container: PlacementId,
  searchQuery: string,
  categories: string[] | undefined,
  initialLayoutType?: LayoutType
) => {
  updatePlacementKeyValueById(container, 'status', 'requested');
  updateMetricByKey(container, PlacementStatus.AdLoaded);

  try {
    const payload: RetailMediaPayload = {
      placement: 'search',
      store: 'finn_recommerce',
      sponsoredProductsCount: 1,
      searchTerm: searchQuery,
      ...(categories && categories.length > 0 && { categories })
    };

    const data = await callApi(payload);
    debugLog('retail_media -> API response:', data);

    updatePlacementKeyValueById(container, 'creativeType', 'native-retail');
    updatePlacementKeyValueById(container, 'status', 'loaded');

    renderSponsoredListingAd(
      container,
      data.sponsoredProducts,
      initialLayoutType
    );
  } catch (error) {
    // Ignore rate-limited requests - the first request will handle rendering
    if (error instanceof RateLimitError) {
      debugLog('retail_media -> Rate-limited request ignored:', error.message);
      return;
    }
    debugLog('retail_media -> Error in request:', error);
    updateMetricByKey(container, PlacementStatus.AdNoFill);
    updatePlacementKeyValueById(container, 'status', 'error');
  }
};

export function triggerRequest(
  container: PlacementId,
  searchQuery: string | string[] | undefined,
  categories: string[] | undefined,
  initialLayoutType?: LayoutType
) {
  const normalizedQuery = Array.isArray(searchQuery)
    ? searchQuery[0]
    : searchQuery;

  if (!normalizedQuery) {
    debugLog('retail_media -> No search query provided, skipping.');
    updateMetricByKey(container, PlacementStatus.AdNoFill);
    updatePlacementKeyValueById(container, 'status', 'error');
    return;
  }
  void request(container, normalizedQuery, categories, initialLayoutType);
}

function renderSponsoredListingAd(
  placementId: PlacementId,
  products?: Array<RetailMediaProduct>,
  initialLayoutType?: LayoutType
) {
  setTimeout(() => {
    const slotElement = document.getElementById(placementId);
    if (!slotElement) {
      debugLog('retail_media -> Slot element not found:', placementId);
      return;
    }

    // Clear the slot
    slotElement.innerHTML = '';
    slotElement.style.gridColumn = '1 / 1';

    // Handle no-fill case
    if (!products || products.length === 0) {
      debugLog('retail_media -> No-fill case for placement:', placementId);
      updateMetricByKey(placementId, PlacementStatus.AdNoFill);
      updatePlacementKeyValueById(placementId, 'status', 'error');
      return;
    }

    const originalLocale = i18n.locale;
    const supportedLocale = ['fi', 'nb', 'da', 'sv'].includes(i18n.locale)
      ? i18n.locale
      : 'nb'; // Default to Norwegian

    if (originalLocale !== supportedLocale) {
      i18n.activate(supportedLocale);
    }

    // Create a container for the React component
    const container = document.createElement('div');
    slotElement.appendChild(container);

    const layout = initialLayoutType ?? ('grid' as LayoutType);

    // Render the React component into the container
    const root = createRoot(container);
    root.render(<SponsoredProductAd layout={layout} products={products} />);

    if (originalLocale !== supportedLocale) {
      setTimeout(() => {
        i18n.activate(originalLocale);
      }, 0);
    }
  }, 1);
}
